import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { adminApi } from '../adminApi';
import { ADMIN_ORDERS_PATH } from '../adminRoutes';
import './AdminOrderDetailPage.css';

export function AdminOrderDetailPage() {
  const navigate = useNavigate();
  const { orderId: orderIdParam } = useParams();
  const [orderIdInput, setOrderIdInput] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const orderId = parseOrderId(orderIdParam);
  const orderQuery = useQuery({
    queryKey: ['admin-order-detail', orderId],
    queryFn: () => {
      if (orderId === null) {
        throw new Error('Некорректный номер заказа.');
      }

      return adminApi.getOrder(orderId);
    },
    enabled: orderId !== null,
    retry: false,
  });

  function handleLookupSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const selectedOrderId = parseOrderId(orderIdInput.trim());

    if (selectedOrderId === null) {
      setLookupError('Введите положительный числовой ID заказа.');
      return;
    }

    setLookupError(null);
    navigate(`${ADMIN_ORDERS_PATH}/${selectedOrderId}`);
  }

  if (orderIdParam === undefined) {
    return (
      <section className="admin-page admin-order-page">
        <header className="admin-page-header">
          <div className="admin-page-header__copy">
            <p className="admin-eyebrow">Управление</p>
            <h1>Заказы</h1>
            <p>Откройте заказ по его ID.</p>
          </div>
        </header>
        <form className="admin-filter-bar admin-order-lookup" onSubmit={handleLookupSubmit}>
          <label className="auth-field">
            <span>ID заказа</span>
            <input min="1" onChange={(event) => setOrderIdInput(event.target.value)} type="number" value={orderIdInput} />
          </label>
          <button className="catalog-button catalog-button_primary" type="submit">Найти</button>
          {lookupError ? <p className="catalog-feedback catalog-feedback_error" role="alert">{lookupError}</p> : null}
        </form>
      </section>
    );
  }

  if (orderId === null) {
    return (
      <section className="admin-page admin-order-page">
        <p className="catalog-feedback catalog-feedback_error" role="alert">Некорректный ID заказа.</p>
        <Link className="catalog-button" to={ADMIN_ORDERS_PATH}>К поиску заказа</Link>
      </section>
    );
  }

  if (orderQuery.isPending) {
    return <section className="admin-page admin-order-page"><p role="status">Загружаем заказ…</p></section>;
  }

  if (orderQuery.isError || !orderQuery.data) {
    return (
      <section className="admin-page admin-order-page">
        <header className="admin-page-header">
          <div className="admin-page-header__copy"><p className="admin-eyebrow">Заказ №{orderId}</p><h1>Заказ не найден</h1></div>
          <Link className="catalog-button" to={ADMIN_ORDERS_PATH}>К поиску заказа</Link>
        </header>
        <p className="catalog-feedback catalog-feedback_error" role="alert">Заказ не найден или недоступен.</p>
      </section>
    );
  }

  const order = orderQuery.data;
  const deliveryDate = order.delivery_date ?? 'Не указана';
  const deliveryTime = order.delivery_time ? `, ${order.delivery_time}` : '';
  const deliveryTimezone = order.delivery_timezone ? ` (${order.delivery_timezone})` : '';

  return (
    <section className="admin-page admin-order-page">
      <header className="admin-page-header">
        <div className="admin-page-header__copy">
          <p className="admin-eyebrow">Заказ №{order.order_id}</p>
          <h1>{order.order_number}</h1>
          <p>{order.status_name}</p>
        </div>
        <Link className="catalog-button" to={ADMIN_ORDERS_PATH}>К поиску заказа</Link>
      </header>

      <div className="admin-order-panels">
        <section className="admin-order-panel" aria-labelledby="admin-order-parties-heading">
          <h2 id="admin-order-parties-heading">Участники и доставка</h2>
          <dl className="admin-order-facts">
            <div><dt>Покупатель</dt><dd>{order.buyer_company_name}</dd></div>
            <div><dt>Поставщик</dt><dd>{order.seller_company_name}</dd></div>
            <div><dt>Адрес доставки</dt><dd>{order.delivery_address_name}: {order.delivery_address}</dd></div>
            <div><dt>Дата и время</dt><dd>{deliveryDate}{deliveryTime}{deliveryTimezone}</dd></div>
            <div><dt>Сумма</dt><dd>{formatAmount(order.total_sum)}</dd></div>
          </dl>
        </section>

        <section className="admin-order-panel" aria-labelledby="admin-order-items-heading">
          <h2 id="admin-order-items-heading">Состав заказа</h2>
          {order.items.length === 0 ? <p>В заказе нет позиций.</p> : (
            <ul className="admin-order-list">
              {order.items.map((orderItem) => (
                <li key={orderItem.item_id}>
                  <div><strong>{orderItem.product_name}</strong><span>{orderItem.quantity} {orderItem.unit_name} × {formatAmount(orderItem.price)}</span></div>
                  <span>{orderItem.is_removed ? 'Исключена' : formatAmount(orderItem.sum)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="admin-order-panel" aria-labelledby="admin-order-history-heading">
          <h2 id="admin-order-history-heading">История заказа</h2>
          {order.history.length === 0 ? <p>История пока пуста.</p> : (
            <ol className="admin-order-list">
              {order.history.map((historyEntry) => (
                <li key={historyEntry.history_id}>
                  <div><strong>{historyEntry.to_status_name ?? 'Изменение заказа'}</strong><span>{historyEntry.actor_user_name ?? historyEntry.actor_company_name ?? 'Система'} · {historyEntry.created_at}</span></div>
                  {historyEntry.comment ? <span>{historyEntry.comment}</span> : null}
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </section>
  );
}

function parseOrderId(value: string | undefined): number | null {
  if (value === undefined || !/^\d+$/.test(value)) {
    return null;
  }

  const orderId = Number(value);

  if (!Number.isSafeInteger(orderId) || orderId < 1) {
    return null;
  }

  return orderId;
}

function formatAmount(value: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value);
}
